// "Call on this computer": the call's own sound (a shared tab, window or
// screen, from getDisplayMedia) mixed with the microphone through Web Audio
// into ONE audio track. The engine records that track like a microphone's
// (same MediaRecorder, mime and timeslice, same takes, upload and recovery);
// the level meter reads the mix. What each browser allows, and why these
// options: /var/tmp/ui-round3-2026-10-29/recorder/capture-support.md.
//
// Called straight from the Start tap: the share is asked for first (it needs
// the tap), with a CaptureController asking Chrome NOT to jump to the shared
// tab (its default), so the microphone prompt that follows shows here, on a
// tab in view. The AudioContext is made before anything is awaited (so it
// runs). The recording starts as soon as the share is chosen: the microphone
// joins the mix when it is allowed ("asking_mic" until then), so a prompt left
// unanswered never holds the call's sound back. The share's ended listeners
// go on before anything else is awaited, and a share already over by then
// counts as gone.
//
// Either side may go while recording: the share ended carries on with the
// microphone (and can be shared again from a tap), the microphone gone or
// muted by the system carries on with the call's sound; both gone ends the
// track, which the engine reads as a recording cut short (a microphone muted
// while it is the only side is passed on as the track's own mute). Each side
// has its own level, so a call that stays silent while the microphone hears
// speech (the wrong tab shared) is said. Stopping the track stops every input
// and closes the AudioContext, so the browser's "sharing" bar goes at once.

import type { CallSoundLike, StreamLike } from "./recorderTypes";
import { CALL_BLOCKED, CALL_CANCELLED, CALL_NO_SOUND, CALL_SILENT_SEC, CALL_UNSUPPORTED, type CallSoundState } from "./sourceModel";
import { QUIET_LEVEL } from "./recorderModel";

const DISPLAY_OPTIONS = {
  // Required by the API; kept tiny and never recorded.
  video: { frameRate: 1 },
  // The person must still hear the call (never suppressLocalAudioPlayback).
  audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false, suppressLocalAudioPlayback: false },
  systemAudio: "include",
  windowAudio: "system",
  selfBrowserSurface: "exclude",
  surfaceSwitching: "include",
  monitorTypeSurfaces: "include",
  preferCurrentTab: false,
};

const MIC = { echoCancellation: true, noiseSuppression: true, autoGainControl: true };

function named(name: string, message: string): Error {
  const e = new Error(message);
  e.name = name;
  return e;
}

type Controller = { setFocusBehavior?: (b: string) => void };

/** Chrome's CaptureController, asked to keep Corvus in front (the microphone prompt shows only on a tab in view). */
function stayHere(): { controller: Controller | null; set: boolean } {
  const Ctor = (globalThis as unknown as { CaptureController?: new () => Controller }).CaptureController;
  if (!Ctor) return { controller: null, set: false };
  const controller = new Ctor();
  try {
    controller.setFocusBehavior?.("no-focus-change");
    return { controller, set: true };
  } catch {
    return { controller, set: false }; // an older Chrome: set once the share is chosen
  }
}

/** A share with sound, or a named error (cancelled, blocked by the system, no sound, unsupported). */
async function askShare(): Promise<MediaStream> {
  const md = navigator.mediaDevices;
  if (!md || typeof md.getDisplayMedia !== "function") throw named(CALL_UNSUPPORTED, "getDisplayMedia is missing");
  const focus = stayHere();
  let display: MediaStream;
  try {
    display = await md.getDisplayMedia({ ...DISPLAY_OPTIONS, ...(focus.controller ? { controller: focus.controller } : {}) } as unknown as DisplayMediaStreamOptions);
  } catch (e) {
    const err = e as Error;
    if (/system/i.test(err?.message ?? "") && err?.name === "NotAllowedError") throw named(CALL_BLOCKED, err.message);
    if (err?.name === "NotAllowedError" || err?.name === "AbortError") throw named(CALL_CANCELLED, err.message);
    throw named(CALL_UNSUPPORTED, err?.message ?? "getDisplayMedia failed");
  }
  if (focus.controller && !focus.set) {
    try { focus.controller.setFocusBehavior?.("no-focus-change"); } catch { /* a window or screen: no focus to keep */ }
  }
  if (display.getAudioTracks().length === 0) {
    display.getTracks().forEach((t) => t.stop());
    throw named(CALL_NO_SOUND, "the share has no audio track");
  }
  return display;
}

/** A side's level, read ten times a second: when it was last loud. */
function levelOf(ctx: AudioContext, src: AudioNode): { loudAt: () => number; stop: () => void } {
  let loud = Date.now();
  try {
    const an = ctx.createAnalyser();
    an.fftSize = 512;
    src.connect(an);
    const buf = new Uint8Array(an.fftSize);
    const t = window.setInterval(() => {
      an.getByteTimeDomainData(buf);
      let sum = 0;
      for (let i = 0; i < buf.length; i++) {
        const v = (buf[i] - 128) / 128;
        sum += v * v;
      }
      if (Math.min(1, Math.sqrt(sum / buf.length) * 4) >= QUIET_LEVEL) loud = Date.now();
    }, 100);
    return { loudAt: () => loud, stop: () => window.clearInterval(t) };
  } catch {
    return { loudAt: () => Date.now(), stop: () => undefined }; // no meter: never warned about
  }
}

export async function captureCallSound(): Promise<StreamLike> {
  // Made inside the tap, before any await, so the browser lets it run.
  const ctx = new AudioContext();
  let display: MediaStream;
  try {
    display = await askShare();
  } catch (e) {
    void ctx.close().catch(() => undefined);
    throw e;
  }
  void ctx.resume().catch(() => undefined);
  const dest = ctx.createMediaStreamDestination();
  const track = dest.stream.getAudioTracks()[0];
  const shares: { stream: MediaStream; level: { loudAt: () => number; stop: () => void } }[] = [];
  let mic: MediaStream | null = null;
  let micLevel: { loudAt: () => number; stop: () => void } | null = null;
  let callLive = false;
  let micAsking = true;
  let micLive = false;
  let micMuted = false;
  let trackMuted = false;
  let closed = false;
  const listeners: Array<() => void> = [];

  const cleanup = () => {
    if (closed) return;
    closed = true;
    for (const s of shares) {
      s.level.stop();
      s.stream.getTracks().forEach((t) => t.stop());
    }
    micLevel?.stop();
    mic?.getTracks().forEach((t) => t.stop());
    void ctx.close().catch(() => undefined);
  };
  const nativeStop = track.stop.bind(track);
  // The engine stops the recorded track on Stop: everything behind it goes with it.
  Object.defineProperty(track, "stop", { configurable: true, value: () => { nativeStop(); cleanup(); } });
  /** Both sides gone: end the track the way a lost microphone ends (the engine offers Carry on). */
  const endAll = () => {
    if (closed) return;
    nativeStop();
    cleanup();
    track.dispatchEvent(new Event("ended"));
  };
  /** After any change: both gone ends the track; the microphone muted while it is the only side mutes it. */
  const changed = () => {
    if (closed) return;
    if (!callLive && !micLive && !micAsking) return endAll();
    const mute = !callLive && micLive && micMuted;
    if (mute !== trackMuted) {
      trackMuted = mute;
      track.dispatchEvent(new Event(mute ? "mute" : "unmute"));
    }
    listeners.forEach((fn) => fn());
  };

  const addShare = (s: MediaStream) => {
    const src = ctx.createMediaStreamSource(new MediaStream(s.getAudioTracks()));
    src.connect(dest);
    const entry = { stream: s, level: levelOf(ctx, src) };
    shares.push(entry);
    let gone = false;
    const shareEnded = () => {
      if (gone || closed) return;
      gone = true;
      entry.level.stop();
      try { src.disconnect(); } catch { /* already */ }
      s.getTracks().forEach((t) => t.stop());
      callLive = shares.some((x) => x !== entry && x.stream.getAudioTracks().some((t) => t.readyState === "live"));
      changed();
    };
    // "Stop sharing" in the browser's bar ends both of the share's tracks.
    for (const t of s.getTracks()) t.addEventListener("ended", shareEnded);
    callLive = true;
    // Ended before its listeners were on: gone already.
    if (s.getAudioTracks().every((t) => t.readyState !== "live")) shareEnded();
    return entry;
  };

  addShare(display);

  // The microphone joins when allowed; the call's sound records meanwhile.
  void navigator.mediaDevices
    .getUserMedia({ audio: MIC })
    .then((m) => {
      micAsking = false;
      if (closed) {
        m.getTracks().forEach((t) => t.stop());
        return;
      }
      mic = m;
      const tracks = m.getAudioTracks();
      if (tracks.length) {
        const src = ctx.createMediaStreamSource(m);
        src.connect(dest);
        micLevel = levelOf(ctx, src);
        micLive = tracks.some((t) => t.readyState === "live");
        micMuted = tracks.every((t) => t.muted);
        for (const t of tracks) {
          t.addEventListener("ended", () => {
            if (closed || !micLive) return;
            micLive = false;
            changed();
          });
          t.addEventListener("mute", () => { micMuted = true; changed(); });
          t.addEventListener("unmute", () => { micMuted = false; changed(); });
        }
      }
      changed();
    })
    .catch(() => {
      micAsking = false; // the call's sound alone still records (said on screen)
      changed();
    });

  const callSound: CallSoundLike = {
    state: (): CallSoundState => {
      const micOn = micLive && !micMuted;
      if (callLive && micAsking) return "asking_mic";
      return callLive && micOn ? "both" : callLive ? "call_only" : "mic_only";
    },
    onChange: (fn) => void listeners.push(fn),
    callSilent: () => {
      if (!callLive || !micLive || micMuted || !micLevel) return false;
      const now = Date.now();
      const callLoud = Math.max(0, ...shares.filter((s) => s.stream.getAudioTracks().some((t) => t.readyState === "live")).map((s) => s.level.loudAt()));
      // the call quiet for a minute while the microphone heard someone in that minute
      return now - callLoud >= CALL_SILENT_SEC * 1000 && micLevel.loudAt() > callLoud && now - micLevel.loudAt() < CALL_SILENT_SEC * 1000;
    },
    async reshare() {
      if (closed) return "cancelled";
      try {
        const s = await askShare();
        if (closed) {
          s.getTracks().forEach((t) => t.stop());
          return "cancelled";
        }
        // The new share replaces any still running (a silent or wrong tab).
        const before = shares.slice();
        addShare(s);
        for (const old of before) old.stream.getTracks().forEach((t) => { t.stop(); t.dispatchEvent(new Event("ended")); });
        changed();
        return "ok";
      } catch (e) {
        return (e as Error).name === CALL_NO_SOUND ? "no_sound" : "cancelled";
      }
    },
  };
  return Object.assign(dest.stream, { callSound }) as unknown as StreamLike;
}
